import { useEffect, useRef, useState } from 'react';
import type { Field, Season } from '../../../../types';
import { getFieldsByFarmer, getSeasonsByFarmer, addField, updateField, deleteField, getState } from '../../../../store';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { EmptyState } from '../../../../components/base/EmptyState';
import { showToast } from '../../../../components/base/Toast';
import { confirmModal } from '../../../../components/base/ConfirmModal';
import { todayInputValue } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { useCategories } from '../../../../hooks/useCategories';
import { useFileUrls } from '../../../../hooks/useFileUrls';
import { uploadFile } from '../../../../api/file';
import { MediaCaptureSheet, type CapturedMedia } from '../../../../components/feature/MediaCaptureSheet';
import { SOIL_TYPE_FALLBACK } from '../../../../constants/categoryFallbacks';
import { DocumentUploadSlot, type ApplyExtracted } from '../../../../components/feature/DocumentUploadSlot';
import { fieldNumber, fieldString } from '../../../../api/aiExtraction';
import { matchCatalogName } from '../../../../utils/catalogMatch';
import { FileThumbnails } from '../../../../components/feature/FileThumbnails';
import { ClearableInput, ClearableTextarea } from '../../../../components/base/ClearableInput';
import { positive, validate } from '../../../../utils/validators';

interface FieldTabProps {
  farmerId: string;
  /** The same plot screens serve the farmer and the HTX; only the right to
   * change them differs (an HTX loses it once the profile is synced to the
   * farmer's account). Viewing, documents included, is always available. */
  canEdit?: boolean;
}

const statusOptions = ['Đang canh tác', 'Tạm dừng', 'Bỏ trống'];

export default function FieldTab({ farmerId, canEdit = true }: FieldTabProps) {
  const store = useStore();
  const { names: soilTypeNames } = useCategories('SOIL_TYPE', SOIL_TYPE_FALLBACK);
  const loaiDatOptions = soilTypeNames.length > 0 ? soilTypeNames : SOIL_TYPE_FALLBACK;
  const fields = getFieldsByFarmer(farmerId);
  const seasons = getSeasonsByFarmer(farmerId);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Field | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  /** Object URL for a just-captured/just-picked photo, so the preview is instant
   * instead of waiting on a round trip to resolve the new file id. */
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<Partial<Field>>({
    name: '',
    maThuaDat: '',
    area: 0,
    location: '',
    loaiDat: loaiDatOptions[0],
    status: 'Đang canh tác',
    hinhAnh: '',
    ghiChu: '',
  });

  // Errors follow the values: a value AI filled in wrong shows red at once, an
  // empty required field once "Lưu" was pressed.
  const [submitted, setSubmitted] = useState(false);
  const allErrors = {
    maThuaDat: validate(form.maThuaDat ?? '', [], true, 'mã thửa đất'),
    name: validate(form.name ?? '', [], true, 'tên thửa đất'),
    area: validate(form.area ? String(form.area) : '', [positive('Diện tích', 10_000)], true, 'diện tích'),
    location: validate(form.location ?? '', [(v) => (v.trim().length < 5 ? 'Vị trí quá ngắn' : undefined)], true, 'vị trí'),
  };
  const err = (k: keyof typeof allErrors, value: unknown) => (value || submitted ? allErrors[k] : undefined);

  /** `hinhAnh` holds a file id; these are the presigned URLs to actually render. */
  const photoUrls = useFileUrls([...fields.map((f) => f.hinhAnh), viewing?.hinhAnh, form.hinhAnh]);
  const formPhotoUrl = localPreview ?? (form.hinhAnh ? photoUrls[form.hinhAnh] : undefined);

  // Object URLs keep the whole image in memory until revoked.
  useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);

  const clearLocalPreview = () => {
    setLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setSubmitted(false);
    clearLocalPreview();
    setForm({
      name: '',
      maThuaDat: '',
      area: 0,
      location: '',
      loaiDat: loaiDatOptions[0],
      status: 'Đang canh tác',
      hinhAnh: '',
      ghiChu: '',
    });
    setModalOpen(true);
  };

  const openEdit = (field: Field) => {
    setEditingId(field.id);
    setSubmitted(false);
    clearLocalPreview();
    setForm({ ...field });
    setModalOpen(true);
  };

  const openView = (field: Field) => {
    setViewing(field);
  };

  const closeView = () => setViewing(null);

  /** Uploads a photo blob and keeps only its file id on the form — the same
   * presign path farm-log attachments use. The field previously stored whatever
   * the farmer typed into an "Ảnh thửa đất (URL)" box, which meant no photo could
   * ever be taken on the spot and the id/URL mix-up left the cards showing a
   * broken image. */
  const attachPhoto = async (blob: Blob, mimeType: string) => {
    setPhotoUploading(true);
    try {
      const fileId = await uploadFile(blob, 'FIELD_PHOTO', mimeType || 'image/jpeg');
      setForm((p) => ({ ...p, hinhAnh: fileId }));
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch {
      showToast('Tải ảnh lên thất bại, vui lòng thử lại', 'error');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleCaptured = (media: CapturedMedia) => attachPhoto(media.blob, media.blob.type || 'image/jpeg');

  const handlePickPhoto = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast('Vui lòng chọn tệp ảnh (JPG/PNG)', 'warning');
      return;
    }
    void attachPhoto(file, file.type);
  };

  const removePhoto = () => {
    clearLocalPreview();
    // Empty string, not undefined: the PATCH body has to carry the cleared value
    // for the server to unset it.
    setForm((p) => ({ ...p, hinhAnh: '' }));
  };

  const seasonCountForField = (fieldId: string) =>
    seasons.filter((s) => s.fieldId === fieldId && s.status !== 'Kết thúc').length;

  // Fills the form from a land title AI read. Inputs already filled are kept
  // unless the user asks to overwrite them.
  const applyLandTitle: ApplyExtracted = (f, { overwrite }) => {
    setForm((p) => {
      const next = { ...p };
      const put = <K extends keyof Field>(key: K, value: Field[K] | undefined) => {
        if (value !== undefined && value !== '' && (overwrite || !p[key])) next[key] = value;
      };
      put('maThuaDat', fieldString(f.code));
      put('name', fieldString(f.name));
      put('area', fieldNumber(f.areaHa));
      put('location', fieldString(f.address));
      put('ghiChu', fieldString(f.description));
      // Loại đất is a catalog select: only a matching entry can be picked.
      const soil = matchCatalogName(fieldString(f.soilType), loaiDatOptions);
      if (soil && (overwrite || !p.loaiDat || p.loaiDat === loaiDatOptions[0])) next.loaiDat = soil;
      return next;
    });
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (Object.values(allErrors).some(Boolean)) {
      showToast('Vui lòng sửa các ô viền đỏ', 'warning');
      return;
    }
    const payload: Omit<Field, 'id'> = {
      farmerId,
      maThuaDat: form.maThuaDat || '',
      name: form.name || '',
      area: Number(form.area) || 0,
      location: form.location || '',
      loaiDat: form.loaiDat || loaiDatOptions[0],
      status: (form.status as Field['status']) || 'Đang canh tác',
      hinhAnh: editingId ? (form.hinhAnh ?? '') : form.hinhAnh || undefined,
      ghiChu: form.ghiChu || undefined,
      taiLieu: form.taiLieu ?? [],
    };
    try {
      if (editingId) {
        await updateField(editingId, payload);
        showToast('Đã cập nhật thửa đất', 'success');
      } else {
        await addField(payload);
        showToast('Đã thêm thửa đất mới', 'success');
      }
      setModalOpen(false);
    } catch {
      showToast('Không thể lưu thửa đất, vui lòng thử lại', 'error');
    }
  };

  const handleDelete = async (field: Field) => {
    const activeSeasons = seasonCountForField(field.id);
    if (activeSeasons > 0) {
      await confirmModal({
        title: 'Không thể xóa',
        message: 'Không thể xóa thửa đất đang có mùa vụ hoạt động.',
        confirmText: 'Đã hiểu',
        cancelText: 'Đóng',
        variant: 'danger',
      });
      return;
    }
    const ok = await confirmModal({
      title: 'Xác nhận xóa',
      message: `Bạn có chắc muốn xóa thửa đất "${field.name}"?`,
      confirmText: 'Xóa',
      cancelText: 'Hủy',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteField(field.id, farmerId);
      showToast('Đã xóa thửa đất', 'success');
    } catch {
      showToast('Không thể xóa thửa đất, vui lòng thử lại', 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">
          Thửa đất ({fields.length})
        </h3>
        {canEdit && <button
          type="button"
          onClick={openCreate}
          className="px-3 py-2 rounded-md text-sm font-medium bg-agrida-700 hover:bg-agrida-800 text-white transition-colors whitespace-nowrap min-h-[44px]"
        >
          <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
            <i className="ri-add-line" />
          </span>
          Thêm thửa đất
        </button>}
      </div>

      {fields.length === 0 ? (
        <EmptyState icon="ri-map-pin-line" title="Chưa có thửa đất" description={canEdit ? 'Hãy thêm thửa đất để bắt đầu quản lý canh tác.' : 'Nông hộ chưa khai báo thửa đất nào.'} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => (
            <div key={field.id} className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{field.name}</p>
                    <StatusBadge status={field.status} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Mã: {field.maThuaDat} · {field.area} ha · {field.loaiDat}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{field.location}</p>
                  {field.ghiChu && <p className="text-xs text-slate-500 mt-1">Ghi chú: {field.ghiChu}</p>}
                  <p className="text-xs text-slate-400 mt-1">
                    <i className="ri-seedling-line mr-1" />
                    {seasonCountForField(field.id)} mùa vụ liên quan
                  </p>
                </div>
                {field.hinhAnh && photoUrls[field.hinhAnh] && (
                  <img
                    src={photoUrls[field.hinhAnh]}
                    alt={field.name}
                    className="w-20 h-20 rounded-xl object-cover shrink-0 border border-slate-100"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => openView(field)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
                >
                  Xem chi tiết
                </button>
                {canEdit && <><button
                  type="button"
                  onClick={() => openEdit(field)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-agrida-700 bg-agrida-50 hover:bg-agrida-100 transition-colors whitespace-nowrap min-h-[44px]"
                >
                  Sửa
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(field)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors whitespace-nowrap min-h-[44px]"
                >
                  Xóa
                </button></>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View modal */}
      {viewing && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={closeView} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-panel">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold text-slate-900">Chi tiết thửa đất</h3>
              <button type="button" onClick={closeView} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {viewing.hinhAnh && photoUrls[viewing.hinhAnh] && (
                <img src={photoUrls[viewing.hinhAnh]} alt={viewing.name} data-testid="field-view-photo" className="w-full h-48 rounded-xl object-cover" />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-400">Mã thửa đất</p><p className="text-sm font-medium">{viewing.maThuaDat}</p></div>
                <div><p className="text-xs text-slate-400">Tên thửa đất</p><p className="text-sm font-medium">{viewing.name}</p></div>
                <div><p className="text-xs text-slate-400">Diện tích</p><p className="text-sm font-medium">{viewing.area} ha</p></div>
                <div><p className="text-xs text-slate-400">Loại đất</p><p className="text-sm font-medium">{viewing.loaiDat}</p></div>
                <div><p className="text-xs text-slate-400">Vị trí</p><p className="text-sm font-medium">{viewing.location}</p></div>
                <div><p className="text-xs text-slate-400">Tình trạng</p><StatusBadge status={viewing.status} /></div>
                <div><p className="text-xs text-slate-400">Số mùa vụ liên quan</p><p className="text-sm font-medium">{seasonCountForField(viewing.id)}</p></div>
              </div>
              {viewing.ghiChu && (
                <div><p className="text-xs text-slate-400">Ghi chú</p><p className="text-sm whitespace-pre-line">{viewing.ghiChu}</p></div>
              )}
              <div>
                <p className="text-xs text-slate-400 mb-1">Giấy tờ đính kèm</p>
                {viewing.taiLieu && viewing.taiLieu.length > 0 ? (
                  <FileThumbnails fileIds={viewing.taiLieu} label="Giấy tờ thửa đất" />
                ) : (
                  <p className="text-sm text-slate-400">Chưa có giấy tờ</p>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3">
              <button
                type="button"
                onClick={closeView}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-panel">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold text-slate-900">{editingId ? 'Sửa thửa đất' : 'Thêm thửa đất'}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <DocumentUploadSlot
                label="Giấy chứng nhận quyền sử dụng đất (không bắt buộc)"
                hint="Tải ảnh sổ đỏ/GCN để AI tự điền mã, tên, diện tích, vị trí và mô tả thửa — kiểm tra lại trước khi lưu."
                docType="LAND_TITLE"
                purpose="LAND_TITLE_DOCUMENT"
                onUploaded={(fileId) => setForm((p) => ({ ...p, taiLieu: [...(p.taiLieu ?? []), fileId] }))}
                onExtracted={applyLandTitle}
              />
              {form.taiLieu && form.taiLieu.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Giấy tờ sẽ lưu cùng thửa đất ({form.taiLieu.length})</p>
                  <FileThumbnails
                    fileIds={form.taiLieu}
                    label="Giấy tờ thửa đất"
                    onRemove={(id) => setForm((p) => ({ ...p, taiLieu: (p.taiLieu ?? []).filter((x) => x !== id) }))}
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Mã thửa đất *</label>
                <ClearableInput value={form.maThuaDat || ''} onValueChange={(v) => setForm((p) => ({ ...p, maThuaDat: v }))} error={err('maThuaDat', form.maThuaDat)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="mã thửa đất" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Tên thửa đất *</label>
                <ClearableInput value={form.name || ''} onValueChange={(v) => setForm((p) => ({ ...p, name: v }))} error={err('name', form.name)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="tên thửa đất" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Diện tích (ha) *</label>
                  <ClearableInput type="number" step="0.01" value={form.area ? String(form.area) : ''} onValueChange={(v) => setForm((p) => ({ ...p, area: v === '' ? 0 : Number(v) }))} error={err('area', form.area)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="diện tích" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Loại đất</label>
                  <select
                    value={form.loaiDat || loaiDatOptions[0]}
                    onChange={(e) => setForm((p) => ({ ...p, loaiDat: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  >
                    {loaiDatOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Vị trí mô tả *</label>
                <ClearableInput value={form.location || ''} onValueChange={(v) => setForm((p) => ({ ...p, location: v }))} error={err('location', form.location)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="vị trí" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Tình trạng</label>
                <select
                  value={form.status || 'Đang canh tác'}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as Field['status'] }))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                >
                  {statusOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Ảnh thửa đất</label>
                {formPhotoUrl ? (
                  <div>
                    <img
                      src={formPhotoUrl}
                      alt="Ảnh thửa đất"
                      data-testid="field-photo-preview"
                      className="w-full h-40 rounded-xl object-cover border border-slate-200 bg-slate-100"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setCaptureOpen(true)}
                        disabled={photoUploading}
                        data-testid="field-photo-capture"
                        className="flex-1 px-3 py-2 rounded-xl text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors min-h-[44px] disabled:opacity-50"
                      >
                        <i className="ri-camera-line mr-1" />
                        Chụp lại
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={photoUploading}
                        data-testid="field-photo-pick"
                        className="flex-1 px-3 py-2 rounded-xl text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors min-h-[44px] disabled:opacity-50"
                      >
                        <i className="ri-image-add-line mr-1" />
                        Chọn ảnh khác
                      </button>
                      <button
                        type="button"
                        onClick={removePhoto}
                        disabled={photoUploading}
                        data-testid="field-photo-remove"
                        className="px-3 py-2 rounded-xl text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors min-h-[44px] disabled:opacity-50"
                      >
                        Xóa ảnh
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCaptureOpen(true)}
                      disabled={photoUploading}
                      data-testid="field-photo-capture"
                      className="flex-1 px-3 py-2 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <i className="ri-camera-line" />
                      Chụp ảnh
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={photoUploading}
                      data-testid="field-photo-pick"
                      className="flex-1 px-3 py-2 rounded-xl text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <i className="ri-image-add-line" />
                      Tải ảnh lên
                    </button>
                  </div>
                )}
                {photoUploading && (
                  <p className="text-xs text-slate-500 mt-2" data-testid="field-photo-uploading">Đang tải ảnh lên...</p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="field-photo-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) handlePickPhoto(f);
                  }}
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Ghi chú</label>
                <ClearableTextarea value={form.ghiChu || ''} onValueChange={(v) => setForm((p) => ({ ...p, ghiChu: v }))} rows={3} maxLength={500} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={photoUploading}
                data-testid="field-save"
                className="px-4 py-2.5 rounded-xl text-sm font-medium bg-agrida-700 hover:bg-agrida-800 disabled:bg-slate-300 text-white transition-colors whitespace-nowrap min-h-[44px]"
              >
                {photoUploading ? 'Đang tải ảnh...' : editingId ? 'Lưu thay đổi' : 'Thêm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaCaptureSheet open={captureOpen} onClose={() => setCaptureOpen(false)} onCaptured={handleCaptured} photoOnly />
    </div>
  );
}